// utils/migrateNotificationToken.ts
//
// Run this ONCE per user after deploying the unified sw.js.
// Old FCM tokens were bound to the now-deleted firebase-messaging-sw.js
// registration and will never receive notifications. This forces a fresh
// token bound to the new unified sw.js.
//
// Usage: call migrateNotificationToken(userId) inside your auth useEffect,
// after the user is confirmed logged in.
//
// Example:
//   useEffect(() => {
//     if (user) migrateNotificationToken(user.uid);
//   }, [user]);

import { notificationService } from '../services/notificationService';
import { safeGetItem, safeSetItem } from './localStorageUtils';

const MIGRATION_KEY = 'fcm_token_migrated_unified_sw_v1';

export async function migrateNotificationToken(userId: string): Promise<void> {
  // Only run once per browser install
  if (safeGetItem(MIGRATION_KEY) === 'done') return;

  // Only migrate if the user has already granted notification permission
  if (Notification.permission !== 'granted') {
    console.log('[TokenMigration] Skipping — notifications not granted');
    safeSetItem(MIGRATION_KEY, 'done'); // don't retry if not granted
    return;
  }

  try {
    console.log('[TokenMigration] Refreshing FCM token for unified SW...');

    // Reset internal state so getFCMToken() fetches fresh
    notificationService.resetMessagingState();

    // Wait for the unified SW to be ready before attempting token fetch
    await navigator.serviceWorker.ready;

    // Mark readiness so addFCMTokenToUser() doesn't bail out early
    await notificationService.waitForMessagingReady();

    // Register the fresh token in Firestore, replacing the stale one
    await notificationService.addFCMTokenToUser(userId, true);

    safeSetItem(MIGRATION_KEY, 'done');
    console.log('[TokenMigration] ✅ FCM token refreshed and saved');
  } catch (error) {
    // Non-fatal: will retry on next app load until it succeeds
    console.warn('[TokenMigration] Token refresh failed, will retry next session:', error);
  }
}